import * as React from 'react'
import { Upload } from 'lucide-react'
import { cn } from '@/lib/cn'
import { Badge } from '@/components/ui/Badge'
import { Text } from '@/components/ui/Text'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/Tooltip'

interface Day0UploadDropzoneProps {
  /** Primary action label, e.g. "Upload entity inventory" */
  label: string
  /** Accepted file extensions for aria-label and input[accept], e.g. ["xlsx", "csv"] */
  acceptedFormats: string[]
  /** true when no upload endpoint is wired */
  disabled?: boolean
  /** Called with the selected or dropped file when not disabled */
  onFile?: (file: File) => void
  className?: string
}

export function Day0UploadDropzone({
  label,
  acceptedFormats,
  disabled = true,
  onFile,
  className,
}: Day0UploadDropzoneProps) {
  const inputRef = React.useRef<HTMLInputElement>(null)
  const [isDragOver, setIsDragOver] = React.useState(false)

  const formatsLabel =
    acceptedFormats.length > 0
      ? acceptedFormats.join(', ')
      : 'all supported formats'

  const accessibleLabel = disabled
    ? `Upload area. Accepts ${formatsLabel}. Upload not yet available.`
    : `Upload area. Accepts ${formatsLabel}. Press Enter or Space to open file picker.`

  function open() {
    if (!disabled) {
      inputRef.current?.click()
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault()
      open()
    }
  }

  const dropzone = (
    <div
      role="button"
      tabIndex={0}
      aria-label={accessibleLabel}
      aria-disabled={disabled ? 'true' : undefined}
      onClick={open}
      onKeyDown={handleKeyDown}
      onDragOver={(e) => {
        e.preventDefault()
        if (!disabled) setIsDragOver(true)
      }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={(e) => {
        e.preventDefault()
        setIsDragOver(false)
        if (!disabled) {
          const file = e.dataTransfer.files[0]
          if (file) onFile?.(file)
        }
      }}
      className={cn(
        'flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed px-6 py-10',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30',
        disabled
          ? 'cursor-not-allowed border-border bg-surface-muted opacity-60'
          : isDragOver
          ? 'border-primary bg-primary/5 cursor-pointer'
          : 'border-border bg-surface hover:border-primary hover:bg-primary/5 cursor-pointer',
        className,
      )}
    >
      <input
        ref={inputRef}
        type="file"
        tabIndex={-1}
        aria-hidden
        className="sr-only"
        accept={acceptedFormats.map((f) => `.${f}`).join(',')}
        disabled={disabled}
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) onFile?.(file)
          e.target.value = ''
        }}
      />

      <Upload size={24} className={disabled ? 'text-text-subtle' : 'text-primary'} aria-hidden />

      <div className="flex flex-col items-center gap-1">
        <Text variant="body-sm" color={disabled ? 'subtle' : 'default'}>
          {label}
        </Text>
        <Text variant="label-sm" color="subtle">
          or drag and drop
        </Text>
      </div>

      {/* Format chips */}
      {acceptedFormats.length > 0 ? (
        <div className="flex flex-wrap justify-center gap-1.5" aria-label="Accepted file formats">
          {acceptedFormats.map((fmt) => (
            <Badge key={fmt} variant="outline">
              {fmt}
            </Badge>
          ))}
        </div>
      ) : (
        <Text variant="label-sm" color="muted">
          All supported formats
        </Text>
      )}
    </div>
  )

  if (disabled) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{dropzone}</TooltipTrigger>
        <TooltipContent>Coming soon</TooltipContent>
      </Tooltip>
    )
  }

  return dropzone
}
